import React, { useState } from 'react'; 
import { 
	Box, 
	Button, 
	FormControl, 
	FormControlLabel, 
	FormLabel, 
	Radio, 
	RadioGroup, 
	TextField, 
	Typography 
} from '@mui/material'; 
import { useTheme } from '@mui/material/styles';
 
const VariableModal = ({ handleClose, onManageVariables, selectedVariable }: any) => { 
	const theme = useTheme();
	const colors = theme.palette;

	const [variable, setVariable] = useState<any>(selectedVariable ?? {}); 
	const [errorText, setErrorText] = useState<string|null>(null); 
 
	const checkMandatoryFields = () => { 
        if(!variable.type || !variable.name || !variable.identifier || !variable.value){ 
            setErrorText('Please fill up all required fields.') 
            return false 
        } 
        return true 
    } 
 
	const handleSaveChannel = () => { 
		const isValid = checkMandatoryFields() 
        if(!isValid) return 
		const uuid = selectedVariable ? selectedVariable.uuid : crypto.randomUUID() 
		onManageVariables({ ...variable, isEnabled: true, uuid }, selectedVariable ? 'update' : 'add'); 
	}; 
 
	const handleVariableChange = ({ name, value }: any) => { 
		setErrorText(null) 
		setVariable({ ...variable, [name]: value }); 
	}; 

	const inputSx = {
		'& .MuiOutlinedInput-root': {
			backgroundColor: `${colors.inputNew.inputBackground} !important`,
			color: `${colors.inputNew.inputText} !important`,

			'& fieldset': {
				borderColor: `${colors.inputNew.inputBorder} !important`,
			},

			'&:hover fieldset': {
				borderColor: `${colors.inputNew.inputBorderHover} !important`,
			},

			'&.Mui-focused fieldset': {
				borderColor: `${colors.inputNew.inputBorderFocus} !important`,
			},

			'& input, & textarea': {
				color: `${colors.inputNew.inputText} !important`,
				WebkitTextFillColor: `${colors.inputNew.inputText} !important`,

				'&::placeholder': {
					color: `${colors.inputNew.inputPlaceholder} !important`,
					WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
					opacity: 1,
				},
			},
		},

		'& .MuiInputBase-input': {
			color: `${colors.inputNew.inputText} !important`,
			WebkitTextFillColor: `${colors.inputNew.inputText} !important`,
		},

		'& .MuiInputLabel-root': {
			color: `${colors.inputNew.inputPlaceholder} !important`,
		},

		'& .MuiInputLabel-root.Mui-focused': {
			color: `${colors.inputNew.inputPlaceholder} !important`,
		},
	};

	const radioSx = {
		padding: '6px',

		'& .MuiSvgIcon-root': {
			color: `${colors.radioGroup.radioBorder} !important`,
		},

		'&.Mui-checked .MuiSvgIcon-root': {
			color: `${colors.radioGroup.radioCheckedBackground} !important`,
		},

		'&.Mui-disabled .MuiSvgIcon-root': {
			color: `${colors.radioGroup.disabledBorder} !important`,
		},
	};

	const radioLabelSx = {
		'&& .MuiFormControlLabel-label.MuiTypography-root': {
			color: `${colors.radioGroup.optionText} !important`,
			fontSize: '14px',
			fontWeight: '500 !important',
		},

		'&& .MuiFormControlLabel-label.Mui-disabled': {
			color: `${colors.radioGroup.disabledBorder} !important`,
		},
	};
 
	return ( 
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 32,
				padding: '24px',
				width: '100%',
				backgroundColor: colors.modal.bg,
				color: colors.inputNew.inputText,
				boxSizing: 'border-box'
			}}
		> 
			<FormControl fullWidth required> 
				<FormLabel
					sx={{
						color: `${colors.radioGroup.title} !important`,
						'&.Mui-focused': {
							color: `${colors.radioGroup.title} !important`
						},
						textAlign: 'left'
					}}
				> 
                	Select Type 
				</FormLabel> 

				<RadioGroup
					row
					name="channelType"
					value={variable.type}
					onChange={(e) => handleVariableChange({name: 'type', value: e.target.value})}
					sx={{
						mt: 1,
						gap: 2,
						flexWrap: 'wrap'
					}}
				> 
					<FormControlLabel 
						value={'static'} 
						control={
							<Radio
								variant="pepwiseRadio"
								sx={radioSx}
							/>
						} 
						label={'Static'}
						sx={radioLabelSx}
					/> 
					<FormControlLabel 
						value={'sql'} 
						control={
							<Radio
								variant="pepwiseRadio"
								sx={radioSx}
							/>
						} 
						label={'SQL'}
						sx={radioLabelSx}
					/> 
					<FormControlLabel 
						value={'cosmos'} 
						control={
							<Radio
								variant="pepwiseRadio"
								sx={radioSx}
							/>
						} 
						label={'Cosmos'}
						sx={radioLabelSx}
					/> 
				</RadioGroup> 
			</FormControl> 
			 
			<Box
				sx={{
					display: 'flex',
					flexDirection: {
						xs: 'column',
						sm: 'row'
					},
					gap: {
						xs: 2,
						sm: 4
					},
					width: '100%'
				}}
			> 
				<FormControl
					required
					sx={{
						width: {
							xs: '100%',
							sm: '50%'
						}
					}}
				> 
					<TextField 
						label="Variable Name*" 
						variant="outlined" 
						size="small" 
						value={variable.name} 
						onChange={(e) => handleVariableChange({ name: 'name', value: e.target.value })} 
						sx={{ 
							width: '100%',
							...inputSx,
						}} 
					/> 
				</FormControl> 

				<FormControl
					required
					sx={{
						width: {
							xs: '100%',
							sm: '50%'
						}
					}}
				> 
					<TextField 
						label="Identifier*" 
						variant="outlined" 
						size="small" 
						value={variable.identifier} 
						onChange={(e) => handleVariableChange({ name: 'identifier', value: e.target.value })} 
						sx={{ 
							width: '100%',
							...inputSx,
						}} 
					/> 
				</FormControl> 
			</Box> 

			<FormControl required fullWidth> 
				<FormLabel
					sx={{
						color: `${colors.inputNew.inputPlaceholder} !important`,
						'&.Mui-focused': {
							color: `${colors.inputNew.inputPlaceholder} !important`
						},
						textAlign: 'left'
					}}
				> 
                    Query / Value 
                </FormLabel> 

				<TextField 
					fullWidth 
					multiline 
					rows={5} 
					variant="outlined" 
					value={variable.value} 
					placeholder={ 
						variable.type === 'sql' 
							? 'SELECT column_name FROM table_name WHERE condition' 
							: variable.type === 'cosmos' 
							? '{"collection": "collection_name", "query": {}, "field": "field_name"}' 
							: 'Enter static value' 
					} 
					onChange={(e) => handleVariableChange({ name: 'value', value: e.target.value })} 
					sx={{
						width: '100%',
						marginTop: 2,
						...inputSx,
					}}
				/> 
			</FormControl> 
 
			<Box
				sx={{
					display: 'flex',
					flexDirection: {
						xs: 'column',
						sm: 'row'
					},
					justifyContent: 'space-between',
					mt: 2
				}}
			> 
                <Typography 
					style={{ 
						color: colors.inputNew.error, 
						cursor: 'pointer', 
						marginTop: '6px', 
						marginRight: '25px' 
					}}					 
				> 
					{errorText ? `*${errorText}` : ''} 
				</Typography> 

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						mt: 2
					}}
				> 
					<Button 
						variant='pepwiseButtonText'
						style={{ 
							color: colors.buttons.primary.color,
							cursor: 'pointer', 
							marginTop: '6px', 
							marginRight: '25px' 
						}} 
						onClick={handleClose} 
					> 
						CANCEL 
					</Button> 

					<Button
						variant="pepwiseSecondary"
						onClick={handleSaveChannel}
						sx={{
							backgroundColor: `${colors.buttons.primary.solidBg} !important`,
							color: `${colors.buttons.primary.solidText} !important`,

							'&:hover': {
								backgroundColor: `${colors.buttons.primary.bgColor} !important`,
								color: `${colors.buttons.primary.solidText} !important`,
							},
						}}
					> 
						SAVE 
					</Button> 
				</Box> 
			</Box> 
		</div> 
	); 
}; 
 
export default VariableModal;